/*
 * Copyright (c) 2020 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import React, {useEffect, useState} from "react";
import './ConditionalLoader.scss';
import {Progress} from "@scuf/common";

/**
 * This represents the condition to be executed.
 * It returns a promise that encapsulates whether the condition is satisfied or not
 */
export interface ICondition {
  /**
   * The function that encapsulates and the condition and returns the evaluation
   * @returns {Promise<boolean>}
   */
  execute: () => Promise<boolean>

  /**
   * The message to display on failure
   */
  failMessage?: string;
}

/**
 * The interface for the conditional loader properties
 * The list of conditions need to be passed as an array
 */
export interface IConditionalLoaderProps {
  /**
   * The title for the loader screen
   */
  title: string;

  /**
   * The list of conditions that need to be satisfied before the component
   * needs to be rendered
   */
  conditions: ICondition[];

  /**
   * Optional Callback handler once the conditions are satisfied
   * @returns {{}}
   */
  onSuccess?: () => {};

  /**
   * Optional Callback handler once the one or mode conditions are satisfied
   * @returns {{}}
   */
  onError?: () => {};

  /**
   * This is the optional callback handler once the conditions are executed.
   * It returns a flag corresponding to accumulation of the result of evaluation for each of the `conditions`
   * in the order they are passed
   * @param {boolean} status
   * @returns {{}}
   */
  onComplete?: (status: boolean) => {};
}

/**
 * The status of the condition evaluation
 */
export enum EvaluationStatus {
  NOT_STARTED, IN_PROGRESS, SUCCESS, FAILED
}

/**
 * This Higher Order Component renders the wrapped component for success subject to successful evaluation
 * of the conditions.
 * It returns error component in case of failure
 * Each of the conditions needs to encapsulate a Promise that returns a boolean.
 * The component will be rendered only when all the conditions have succeeded. A callback handler will be triggered
 * upon completion of evaluation (success or failure).
 * @param {React.ComponentType<P extends object>} SuccessComponent
 * @param {React.ComponentType<P extends object>} ErrorComponent
 * @returns {React.FC<P & IConditionalLoaderProps>}
 */
// @Todo
  // 1. Ability to return the status of evaluation of each individual conditions
  // 2. Right now all conditions are evaluated in parallel. Shoud be able to specify any dependencies.
  // This may need the conditions object to be more sophisticated than an array
  // (may be a graph that can be topologically sorted). May be its not needed. If user wants such a functionality,
  // he can wrap a conditional loader inside another ?
export const loadOnConditionsSatisfied = <P extends object, R extends object>(SuccessComponent: React.ComponentType<P>,
                                                                              ErrorComponent: React.ComponentType<R>):
  React.FC<P & IConditionalLoaderProps> | React.FC<R & IConditionalLoaderProps> =>
    ({
       title,
       conditions,
       onComplete,
       ...props
     }: IConditionalLoaderProps) => {

      /**
       * Initialize the state of the HOC, setting the flag to load the component to false
       */
      const [evaluationStatus, setEvaluationStatus] = useState(EvaluationStatus.NOT_STARTED);

      /**
       * Execute the conditions that are necessary for the component to render.
       * Specify o dependencies
       */
      useEffect(() => {
        let evaluateConditions = async () => {
          setEvaluationStatus(EvaluationStatus.IN_PROGRESS);

          let evaluationPromises = conditions.map((condition: ICondition) => {
            return condition.execute();
          });

          let evaluations = await Promise.all(evaluationPromises);
          return evaluations.reduce((temp, evaluation) => temp && evaluation);
        };
        let completeCallback = (!!onComplete) ? onComplete : () => {
        };
        if (conditions.length > 0) {
          evaluateConditions().then((evaluatedStatus: boolean) => {
            setEvaluationStatus(evaluatedStatus ? EvaluationStatus.SUCCESS : EvaluationStatus.FAILED);
            completeCallback(evaluatedStatus);
          }).catch((error) => {
            // In practice this signals an error condition rather than evaluation to false
            // @Todo This might need some thinking and reconsideration. may be just throwing an error is not an alternative.
            setEvaluationStatus(EvaluationStatus.FAILED);
            completeCallback(false);
          });
        } else {
          // When there are no conditions, its always a success
          setEvaluationStatus(EvaluationStatus.SUCCESS);
          completeCallback(true);
        }
      }, []);

      /**
       * The `render` implementation
       */
      return (
        (evaluationStatus === EvaluationStatus.NOT_STARTED || evaluationStatus === EvaluationStatus.IN_PROGRESS) ?
          evaluationPlaceHolder(title, 'ui-cl-container', true)
          : (
            (evaluationStatus === EvaluationStatus.SUCCESS) ?
              <SuccessComponent {...props as P} />
              : <ErrorComponent {...props as R}/>
          ));
    };

/**
 * The place holder component while the conditions are being evaluated.
 * @param {string} title
 * @param {string} className
 * @param {boolean} evaluating
 * @returns {JSX.Element}
 */
export const evaluationPlaceHolder = (title: string, className: string = '', evaluating: boolean = true): JSX.Element =>
  <div className={`application-page-content-no-nav ${className}`}>
    <div className='ui-cl-title'>
      {title}
    </div>
    <div className='ui-cl-block'>
      {
        (evaluating) ?
          <Progress circular={true}
                    size={"xlarge"}
                    className='ui-cl-progress'/>
          : <></>
      }
    </div>
  </div>;
